import { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ArrowLeft, Coins, PlusCircle, GraduationCap, BookOpen, Layers,
  Calendar, Trophy, BarChart3, Timer, Hourglass, ExternalLink, Settings,
  Globe, FileText
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { resolveBackNavigation } from '@/lib/utils'
import { ApiTokenManagerCard } from '@/components/shared/ApiTokenManagerCard'

interface ExamInfo { id: number; name: string; code: string }
interface TaxonomyItem { id: number; name: string }

interface UserDetail {
  id: number
  name: string
  email: string
  role: string
  preferred_language: string
  balance: number
  standard?: TaxonomyItem | null
  enrolled_exams: ExamInfo[]
  mapped_subjects?: TaxonomyItem[]
  mapped_syllabi?: TaxonomyItem[]
  api_token?: string | null
  created_at: string
}

interface PerformanceAnalytics {
  total_attempts: number
  completed_attempts_count: number
  in_progress_attempts_count: number
  average_accuracy: number
  highest_score: number
  average_score: number
  avg_time_spent_seconds: number
  total_time_spent_seconds: number
  credits_granted: number
  credits_spent: number
}

interface TestAttemptItem {
  id: number
  test_id: number
  test_title: string
  test_type: string
  status: string
  total_score: number
  max_score: number
  accuracy: number
  time_spent_seconds: number
  completed_at: string
}

interface Transaction {
  id: number
  amount: number
  kind: string
  description: string
  created_at: string
  created_by: string | null
}

interface Props {
  user: UserDetail
  performance_analytics: PerformanceAnalytics
  test_attempts: TestAttemptItem[]
  transactions: Transaction[]
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0m'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60)
    const remMins = mins % 60
    return `${hrs}h ${remMins}m`
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

export default function AdminUsersShow({ user, performance_analytics, test_attempts, transactions }: Props) {
  const { toast } = useToast()
  const [grantAmount, setGrantAmount] = useState('50')
  const [grantReason, setGrantReason] = useState('Admin Grant')
  const [submitting, setSubmitting] = useState(false)

  // Edit Configuration Modal State
  const [editConfigModalOpen, setEditConfigModalOpen] = useState(false)
  const [editName, setEditName] = useState(user.name)
  const [editRole, setEditRole] = useState(user.role)
  const [editLanguage, setEditLanguage] = useState(user.preferred_language || 'en')
  const [updatingRole, setUpdatingRole] = useState(false)

  const handleGrantCredits = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.post(`/admin/users/${user.id}/credits`, {
      amount: grantAmount,
      description: grantReason
    }, {
      onSuccess: () => {
        toast({ title: 'Credits granted', description: `${grantAmount} credits granted to ${user.name}.` })
        setGrantAmount('50')
      },
      onFinish: () => setSubmitting(false)
    })
  }

  const handleSaveUserConfig = (e: React.FormEvent) => {
    e.preventDefault()
    setUpdatingRole(true)
    router.patch(`/admin/users/${user.id}`, {
      user: {
        name: editName,
        role: editRole,
        preferred_language: editLanguage
      }
    }, {
      onSuccess: () => {
        toast({ title: 'User configuration updated' })
        setEditConfigModalOpen(false)
      },
      onFinish: () => setUpdatingRole(false)
    })
  }

  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/users', 'Back to Users')

  const currentPathWithSearch = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/users/${user.id}`

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Navigation Top Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={backHref}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {backLabel}
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Overview Banner */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{user.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-0.5">{user.email}</p>
                  </div>
                  <Badge variant={user.role === 'admin' ? 'default' : user.role === 'reviewer' ? 'secondary' : 'outline'} className="capitalize">
                    {user.role}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Coins className="h-3.5 w-3.5 text-primary" /> Credit Balance
                    </div>
                    <div className="text-xl font-bold text-foreground mt-0.5">{user.balance}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5 text-secondary" /> Total Attempts
                    </div>
                    <div className="font-semibold text-lg mt-0.5">{performance_analytics.total_attempts}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" /> Preferred Lang
                    </div>
                    <div className="font-semibold uppercase text-lg mt-0.5">{user.preferred_language || 'EN'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" /> Joined Date
                    </div>
                    <div className="text-xs font-semibold mt-1.5">{user.created_at}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Test Attempt History Table */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Test Attempt History ({test_attempts.length})</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Test Title</TableHead>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead className="w-28">Score</TableHead>
                      <TableHead className="w-24">Accuracy</TableHead>
                      <TableHead className="w-24">Time</TableHead>
                      <TableHead className="w-36">Attempted At</TableHead>
                      <TableHead className="w-12 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {test_attempts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                          No test attempts recorded for this user yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      test_attempts.map((att) => (
                        <TableRow key={att.id}>
                          <TableCell className="font-medium max-w-xs">
                            <Link href={`/admin/tests/${att.test_id}?return_to=${encodeURIComponent(currentPathWithSearch)}`} className="hover:underline line-clamp-1">
                              {att.test_title}
                            </Link>
                          </TableCell>
                          <TableCell className="text-xs capitalize">{att.test_type.replace('_', ' ')}</TableCell>
                          <TableCell className="text-xs font-semibold">
                            {att.total_score} / {att.max_score} pts
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs font-semibold">
                              {att.accuracy ?? 0}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">{formatDuration(att.time_spent_seconds)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{att.completed_at}</TableCell>
                          <TableCell className="text-right">
                            <Button size="icon" variant="ghost" asChild title="View Test Attempt Result">
                              <Link href={`/student/attempts/${att.id}/result?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Transactions Audit Log */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Credit Transaction Audit Log</CardTitle>
              </CardHeader>
              <CardContent className="divide-y p-0">
                {transactions.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">No credit transactions found.</p>
                ) : (
                  transactions.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-4 text-sm">
                      <div>
                        <p className="font-medium">{t.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {t.created_at} {t.created_by && `by ${t.created_by}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">{t.kind}</Badge>
                        <span className={`font-bold ${t.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {t.amount > 0 ? `+${t.amount}` : t.amount}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar Column */}
          <div className="space-y-6">
            {/* 1. User Configuration Card (TOP FIRST with Settings Cog Modal Launcher) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center justify-between">
                  <span>User Configuration</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setEditConfigModalOpen(true)}
                    title="Edit User Configuration"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <div className="flex justify-between border-b pb-2 items-center">
                  <span className="text-muted-foreground">Account Role:</span>
                  <Badge variant={user.role === 'admin' ? 'default' : user.role === 'reviewer' ? 'secondary' : 'outline'} className="capitalize font-semibold">
                    {user.role}
                  </Badge>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <span className="text-muted-foreground">Preferred Language:</span>
                  <span className="font-semibold uppercase">{user.preferred_language || 'English (en)'}</span>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <span className="text-muted-foreground">Account Status:</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">
                    Active
                  </Badge>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">Registered On:</span>
                  <span className="font-medium">{user.created_at}</span>
                </div>
              </CardContent>
            </Card>

            {/* 2. Standalone Dedicated Grant Test Credits Card */}
            <Card className="border-2 border-primary/20 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <PlusCircle className="h-4 w-4 text-primary" />
                  <span>Grant Test Credits</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGrantCredits} className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Credit Amount</Label>
                    <Input
                      type="number"
                      min="1"
                      value={grantAmount}
                      onChange={(e) => setGrantAmount(e.target.value)}
                      placeholder="Amount e.g. 50"
                      className="h-8 text-xs mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Reason / Note</Label>
                    <Input
                      value={grantReason}
                      onChange={(e) => setGrantReason(e.target.value)}
                      placeholder="e.g. Scholarship / Promo reward"
                      className="h-8 text-xs mt-1"
                    />
                  </div>
                  <Button type="submit" size="sm" className="w-full h-8 text-xs font-semibold" disabled={submitting}>
                    {submitting ? 'Granting…' : 'Grant Credits'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* 3. API Access & Developer Token Card */}
            <ApiTokenManagerCard
              apiToken={user.api_token}
              regenerateUrl={`/admin/users/${user.id}/regenerate_api_token`}
              title="API Access & Developer Token"
              description={`Manage API developer token for ${user.name}.`}
            />

            {/* 4. Taxonomy & Mapping Context Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Taxonomy & Mappings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* Enrolled Exams */}
                <div>
                  <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Enrolled Exams ({user.enrolled_exams.length})</span>
                  {user.enrolled_exams.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No active exam enrollments.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {user.enrolled_exams.map((ex) => (
                        <Button key={ex.id} variant="outline" size="sm" className="h-7 text-xs gap-1" asChild>
                          <Link href={`/admin/exams/${ex.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <GraduationCap className="h-3 w-3 text-indigo-500" />
                            <span>{ex.name}</span>
                            <ExternalLink className="h-2.5 w-2.5 text-muted-foreground ml-0.5" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Target Standard */}
                <div>
                  <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Target Standard</span>
                  {user.standard ? (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" asChild>
                      <Link href={`/admin/standards/${user.standard.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                        <Layers className="h-3 w-3 text-purple-500" />
                        <span>{user.standard.name}</span>
                        <ExternalLink className="h-2.5 w-2.5 text-muted-foreground ml-0.5" />
                      </Link>
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Unassigned</span>
                  )}
                </div>

                {/* Mapped Subjects */}
                {user.mapped_subjects && user.mapped_subjects.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Subjects ({user.mapped_subjects.length})</span>
                    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pt-0.5">
                      {user.mapped_subjects.map((s) => (
                        <Button key={s.id} variant="outline" size="sm" className="h-7 text-xs gap-1" asChild>
                          <Link href={`/admin/subjects/${s.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <BookOpen className="h-3 w-3 text-emerald-500" />
                            <span className="truncate max-w-[140px]">{s.name}</span>
                            <ExternalLink className="h-2.5 w-2.5 text-muted-foreground ml-0.5" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mapped Syllabi */}
                {user.mapped_syllabi && user.mapped_syllabi.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Syllabi ({user.mapped_syllabi.length})</span>
                    <div className="flex flex-wrap gap-1.5">
                      {user.mapped_syllabi.map((sy) => (
                        <Button key={sy.id} variant="outline" size="sm" className="h-7 text-xs gap-1" asChild>
                          <Link href={`/admin/syllabi/${sy.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <BookOpen className="h-3 w-3 text-amber-500" />
                            <span>{sy.name}</span>
                            <ExternalLink className="h-2.5 w-2.5 text-muted-foreground ml-0.5" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 4. Student Performance Analytics Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center justify-between">
                  <span>Student Performance Analytics</span>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* Accuracy Gauge Bar */}
                <div className="space-y-1.5 bg-accent/40 p-3 rounded-lg border">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-medium text-muted-foreground">Average Accuracy Percentage</span>
                    <span className="font-bold text-foreground">{performance_analytics.average_accuracy}%</span>
                  </div>
                  <Progress value={performance_analytics.average_accuracy || 0} className="h-2" />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Completed <span className="font-semibold text-foreground">{performance_analytics.completed_attempts_count}</span> of {performance_analytics.total_attempts} attempts
                  </p>
                </div>

                {/* Score Stats Grid */}
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="p-2 border rounded-md bg-card shadow-2xs">
                    <Trophy className="h-3.5 w-3.5 text-amber-500 mx-auto mb-1" />
                    <div className="text-[10px] text-muted-foreground">Highest Score</div>
                    <div className="font-bold text-sm text-foreground">{performance_analytics.highest_score} pts</div>
                  </div>
                  <div className="p-2 border rounded-md bg-card shadow-2xs">
                    <BarChart3 className="h-3.5 w-3.5 text-blue-500 mx-auto mb-1" />
                    <div className="text-[10px] text-muted-foreground">Average Score</div>
                    <div className="font-bold text-sm text-foreground">{performance_analytics.average_score} pts</div>
                  </div>
                </div>

                {/* Time Spent Analytics */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2.5 border rounded-md bg-card shadow-2xs">
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                      <Timer className="h-3 w-3 text-indigo-500" /> Avg Time
                    </div>
                    <div className="font-semibold text-sm">{formatDuration(performance_analytics.avg_time_spent_seconds)}</div>
                  </div>
                  <div className="p-2.5 border rounded-md bg-card shadow-2xs">
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                      <Hourglass className="h-3 w-3 text-emerald-500" /> Total Time
                    </div>
                    <div className="font-semibold text-sm">{formatDuration(performance_analytics.total_time_spent_seconds)}</div>
                  </div>
                </div>

                {/* Credit Summary */}
                <div className="pt-3 border-t space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Coins className="h-3.5 w-3.5 text-amber-500" /> Credit Balance
                    </span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 font-bold">
                      {user.balance} Credits
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit User Configuration Modal */}
      <Dialog open={editConfigModalOpen} onOpenChange={setEditConfigModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Configuration</DialogTitle>
            <DialogDescription>
              Update user details and account role safely.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveUserConfig} className="space-y-4">
            <div>
              <Label className="text-xs">Full Name</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Account Role</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {['student', 'reviewer', 'admin'].map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant={editRole === r ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditRole(r)}
                    className="capitalize text-xs h-8"
                  >
                    {r}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Preferred Language</Label>
              <Input
                value={editLanguage}
                onChange={(e) => setEditLanguage(e.target.value)}
                placeholder="e.g. en or hi"
                className="mt-1 uppercase"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditConfigModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updatingRole}>
                {updatingRole ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  )
}
